import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Wrench, ClipboardText, Hammer, Gauge, Pulse, Package, Notepad, Trash, Receipt } from "@phosphor-icons/react";
import { fmtD, gbp, titleCase } from "./assetUtils";

const ICONS = { service: Wrench, inspection: ClipboardText, repair: Hammer, meter_reading: Gauge, condition: Pulse, parts: Package, note: Notepad };
const TONES = {
  service: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400", inspection: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400",
  repair: "bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400", meter_reading: "bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400",
  condition: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400", parts: "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-400", note: "bg-muted text-muted-foreground",
};

export function AssetLogList({ logs, unit, canDelete, onDelete, showCost = true, emptyText = "Nothing logged yet — services, inspections and readings will build the history here." }) {
  if (!logs?.length) return <p className="text-sm text-muted-foreground py-6 text-center" data-testid="asset-logs-empty">{emptyText}</p>;
  return (
    <ol className="relative border-l border-border ml-3 space-y-4" data-testid="asset-log-list">
      {logs.map((l) => {
        const Icon = ICONS[l.type] || Notepad;
        return (
          <li key={l.id} className="ml-5" data-testid={`asset-log-${l.id}`}>
            <span className={`absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full ${TONES[l.type] || TONES.note}`}><Icon size={13} weight="bold" /></span>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium leading-tight">{l.summary || titleCase(l.type)}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {titleCase(l.type)} · {fmtD(l.date)}{l.performed_by ? ` · ${l.performed_by}` : ""}
                  {l.job_reference && <> · <Link to={`/jobs/${l.job_id}`} className="font-mono text-primary hover:underline">{l.job_reference}</Link></>}
                </p>
                {l.details && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{l.details}</p>}
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {l.meter_reading != null && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted font-mono">{Number(l.meter_reading).toLocaleString()} {unit || ""}</span>}
                  {l.condition_after && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted capitalize">Condition → {l.condition_after}</span>}
                  {(l.parts || []).map((p) => <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-muted">{p}</span>)}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {showCost && l.cost > 0 && <span className="text-xs font-semibold inline-flex items-center gap-1"><Receipt size={12} /> {gbp(l.cost)}</span>}
                {canDelete && <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => onDelete(l)} data-testid={`asset-log-delete-${l.id}`}><Trash size={12} /></Button>}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
